import { prefersReducedMotion } from '@theme/utilities';

/**
 * The Instagram feed of the Instafeed app as a carousel (sections/_blocks.liquid, the section's
 * settings Instagram feed as a carousel on mobile, and on tablet and desktop). The stylesheet
 * lays the app's posts out in one row that the browser scrolls and snaps.
 *
 * On a phone the app's own slider still listens for touches - on its row and on the document -
 * and cancels the swipe to move its row its own way. Caught on the way down, the touches on the
 * row reach neither listener, and the browser scrolls it. A tap still opens a post: the click is
 * left to the app.
 *
 * On a tablet and a desktop the row is the app's feed itself, its title the first slide; the
 * progress bar and the two round arrows under it (snippets/carousel-progress.liquid, standalone)
 * follow and move it. A video post plays while the pointer is over it: the app keeps the posts'
 * video addresses to itself, so they are read once from the same feed the app reads, and matched
 * to the posts by their pictures. From 990px the title's widest line is measured, for the
 * stylesheet to stand the title in the middle of its slide; on a phone, where the title is as large
 * as the Best sellers title, it is fitted to the width. At every width the word Instagram in the
 * title is wrapped for the stylesheet to set it in the shop's yellow.
 */
class InstagramCarousel extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;

  /** @type {MutationObserver | null} */
  #observer = null;

  /** @type {HTMLElement | null} */
  #feed = null;

  /** @type {Promise<Map<string, string>> | null} */
  #videos = null;

  #phone = window.matchMedia('(max-width: 749px)');

  #pointer = window.matchMedia('(hover: hover)');

  #desktop = window.matchMedia('(min-width: 990px)');

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    for (const type of ['touchstart', 'touchmove', 'touchend']) {
      this.addEventListener(type, this.#hold, { capture: true, passive: true, signal });
    }

    // The app writes the feed in after the page loads, and may write it again.
    this.#observer = new MutationObserver(this.#attach);
    this.#observer.observe(this, { childList: true, subtree: true });

    // The title's size follows the window's width, and its font may arrive after the feed.
    window.addEventListener('resize', this.#fitTitle, { passive: true, signal });
    this.#desktop.addEventListener('change', this.#fitTitle, { signal });
    document.fonts.addEventListener('loadingdone', this.#fitTitle, { signal });

    if (this.classList.contains('instagram-carousel--wide')) {
      this.addEventListener('click', this.#step, { signal });
      this.addEventListener('pointerover', this.#play, { signal });
      this.addEventListener('pointerout', this.#stop, { signal });
      window.addEventListener('resize', this.#update, { passive: true, signal });
    }

    this.#attach();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
    this.#observer?.disconnect();
    this.#observer = null;
    this.#feed = null;
  }

  /** @param {TouchEvent} event */
  #hold = (event) => {
    if (!this.#phone.matches || !this.classList.contains('instagram-carousel--phone')) return;
    if (!(event.target instanceof Element) || !event.target.closest('.instafeed-new-layout-container')) return;

    event.stopPropagation();
  };

  #attach = () => {
    const feed = this.querySelector('#insta-feed');
    if (!(feed instanceof HTMLElement)) return;
    this.#markTitle(feed);
    this.#fitTitle();
    if (!this.classList.contains('instagram-carousel--wide')) return;
    if (feed !== this.#feed) {
      this.#feed = feed;
      feed.addEventListener('scroll', this.#update, { passive: true, signal: this.#controller?.signal });
    }
    this.#update();
  };

  /**
   * The word Instagram in the title (in any language: INSTAGRAM, INSTAGRAMIE) in the shop's yellow,
   * once: the stylesheet colours .instagram-carousel__accent.
   * @param {HTMLElement} feed
   */
  #markTitle(feed) {
    const title = feed.querySelector(':scope > h2');
    if (!(title instanceof HTMLElement) || title.querySelector('.instagram-carousel__accent')) return;

    const walker = document.createTreeWalker(title, NodeFilter.SHOW_TEXT);
    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
      const text = node.textContent || '';
      const match = /instagram\p{L}*/iu.exec(text);
      if (!match) continue;
      const word = document.createElement('span');
      word.className = 'instagram-carousel__accent';
      word.textContent = match[0];
      const rest = /** @type {Text} */ (node).splitText(match.index);
      rest.textContent = text.slice(match.index + match[0].length);
      rest.before(word);
      return;
    }
  }

  /**
   * From 990px the width of the title's widest line, as --instagram-title-width. Below 990px, where
   * the title fills its width (a phone: as large as the Best sellers title), --instagram-title-fit:
   * the share of that size its longest word fits the width at (INSTAGRAMIE, say), 1 when it fits.
   */
  #fitTitle = () => {
    const title = this.querySelector('#insta-feed > h2');
    if (!(title instanceof HTMLElement)) return;

    // Measured at the full size and width, where the lines break as they will.
    title.style.removeProperty('--instagram-title-width');
    title.style.removeProperty('--instagram-title-fit');

    const range = document.createRange();
    range.selectNodeContents(title);
    const widest = Math.max(0, ...Array.from(range.getClientRects(), (line) => line.width));
    if (!(widest > 0)) return;

    if (this.#desktop.matches) {
      title.style.setProperty('--instagram-title-width', `${Math.ceil(widest)}px`);
      return;
    }
    const style = getComputedStyle(title);
    const room = title.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
    if (widest > room) title.style.setProperty('--instagram-title-fit', String(Math.floor((room / widest) * 1000) / 1000));
  };

  /** The fill and the arrows follow where the row is scrolled to. */
  #update = () => {
    const feed = this.#feed;
    if (!feed) return;

    const max = feed.scrollWidth - feed.clientWidth;
    const at = Math.abs(feed.scrollLeft);
    const fill = this.querySelector('[data-carousel-fill]');
    if (fill instanceof HTMLElement) fill.style.setProperty('--progress', String(max > 0 ? at / max : 0));

    for (const button of this.querySelectorAll('[data-carousel-step]')) {
      if (!(button instanceof HTMLButtonElement)) continue;
      const back = Number(button.dataset.carouselStep) < 0;
      button.disabled = back ? at <= 1 : at >= max - 1;
    }
  };

  /** An arrow moves the row on by one post. @param {MouseEvent} event */
  #step = (event) => {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('[data-carousel-step]');
    const feed = this.#feed;
    if (!(button instanceof HTMLElement) || !feed) return;

    const post = feed.querySelector('.instafeed-new-layout-item');
    const gap = parseFloat(getComputedStyle(feed).columnGap) || 0;
    const width = post ? post.getBoundingClientRect().width + gap : feed.clientWidth * 0.8;
    const direction = getComputedStyle(feed).direction === 'rtl' ? -1 : 1;

    feed.scrollBy({
      left: Number(button.dataset.carouselStep) * width * direction,
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });
  };

  /** @param {PointerEvent} event */
  #play = async (event) => {
    if (this.#phone.matches || !this.#pointer.matches || !(event.target instanceof Element)) return;
    const post = event.target.closest('.instafeed-new-layout-item');
    if (!(post instanceof HTMLElement) || !post.querySelector(".instafeed-hover-layer[data-feed-id$='-video']")) return;

    let video = post.querySelector('video.instagram-carousel__video');
    if (!(video instanceof HTMLVideoElement)) {
      const picture = post.querySelector('img');
      const source = picture && (await this.#videoFor(picture));
      if (!source || !post.matches(':hover') || post.querySelector('video.instagram-carousel__video')) return;

      video = document.createElement('video');
      video.className = 'instagram-carousel__video';
      video.muted = true;
      video.loop = true;
      video.playsInline = true;
      video.preload = 'auto';
      video.setAttribute('aria-hidden', 'true');
      video.src = source;
      picture.after(video);
    }

    video.hidden = false;
    video.play().catch(() => {
      // A video that will not play leaves the picture, which is still there.
    });
  };

  /** @param {PointerEvent} event */
  #stop = (event) => {
    if (!(event.target instanceof Element)) return;
    const post = event.target.closest('.instafeed-new-layout-item');
    if (!(post instanceof HTMLElement)) return;
    if (event.relatedTarget instanceof Node && post.contains(event.relatedTarget)) return;

    const video = post.querySelector('video.instagram-carousel__video');
    if (!(video instanceof HTMLVideoElement)) return;
    video.pause();
    video.hidden = true;
  };

  /**
   * The address of a post's video, by its picture.
   * @param {HTMLImageElement} picture
   * @returns {Promise<string | undefined>}
   */
  async #videoFor(picture) {
    this.#videos ??= this.#readFeed();
    const videos = await this.#videos;
    const key = (picture.dataset.instafeedSrc || picture.currentSrc || picture.src).split('?')[0];
    return videos.get(key);
  }

  /**
   * The same feed the app reads (its settings are on the page), as a map of picture to video.
   * @returns {Promise<Map<string, string>>}
   */
  async #readFeed() {
    const videos = new Map();
    // @ts-ignore - the app's own global
    const options = window.instafeedSettings?.instances?.[0]?.options;
    if (!options?.hash) return videos;

    const path = window.location.pathname;
    let handle = '';
    if (path.includes('/products/')) handle = path.split('/products/')[1] || '';
    else if (path.includes('/collections/')) handle = path.split('/collections/')[1] || '';

    const url = new URL(`https://instafeed.nfcube.com/feed/v${options.apiVersion}`);
    url.search = new URLSearchParams({
      limit: String(options.picturesNeeded),
      account: options.shopOrigin,
      fu: '0',
      fid: String(options.feedId ?? 0),
      hash: options.hash,
      locale: options.locale,
      handle: handle.split(/[?&#/]/)[0] || '',
    }).toString();

    try {
      const response = await fetch(url);
      if (!response.ok) return videos;
      const { data } = await response.json();
      for (const post of data || []) {
        const video = post?.videos?.standard_resolution?.url;
        const image = post?.images?.standard_resolution?.url;
        if (video && image) videos.set(image.split('?')[0], video);
      }
    } catch {
      // Without the feed the posts stay pictures.
    }
    return videos;
  }
}

if (!customElements.get('instagram-carousel')) {
  customElements.define('instagram-carousel', InstagramCarousel);
}
